import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Play, 
  Wifi, 
  Calendar, 
  Users, 
  Building2, 
  ArrowRight, 
  Search, 
  Radio, 
  Film,
  Sparkles
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

const SobreNosotrosPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const features = [
    {
      icon: Search,
      title: 'Explorar y Contratar Contenidos',
      description: 'Accede a nuestro extenso catálogo y gestiona la programación de tu sala de forma sencilla.',
      color: '#e4002b'
    },
    {
      icon: Wifi,
      title: 'Recepción Vía Streaming',
      description: 'Olvídate de los envíos físicos. Bravo facilita la recepción de los contenidos en alta calidad directamente en tu sala a través de streaming, optimizando la operativa técnica.',
      color: '#4ecdc4'
    },
    {
      icon: Sparkles,
      title: 'Ampliar tu Oferta',
      description: 'Enriquece la cartelera de tu cine o la programación de tu auditorio con eventos exclusivos que atraen a nuevos públicos.',
      color: '#ff6b35'
    }
  ];

  const stats = [
    { number: '15+', label: 'Años de experiencia', icon: Calendar },
    { number: '125+', label: 'Cines asociados', icon: Building2 },
    { number: '250k+', label: 'Espectadores alcanzados', icon: Users },
  ];

  const contentTypes = [
    { icon: Radio, label: 'Ópera', color: '#e4002b' },
    { icon: Play, label: 'Ballet', color: '#ff6b35' },
    { icon: Film, label: 'Conciertos', color: '#4ecdc4' },
    { icon: Sparkles, label: 'Documentales', color: '#9b59b6' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="sobre-nosotros-page">
      <Header onOpenAuth={() => setShowAuth(true)} onOpenSearch={() => setShowSearch(true)} />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1761502479994-3a5e07ec243e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxjaW5lbWElMjBwcm9qZWN0b3IlMjBzdHJlYW1pbmclMjB0ZWNobm9sb2d5JTIwZGlnaXRhbHxlbnwwfHx8fDE3NzA3NDUwMTN8MA&ixlib=rb-4.1.0&q=85" 
            alt="Sala de cine moderna"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-1 bg-[#e4002b]" />
              <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Sobre Nosotros</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Conectando Tu Sala con los
              <span className="text-[#e4002b]"> Mejores Contenidos</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
              Bravo es la solución tecnológica desarrollada por <span className="text-white font-semibold">Versión Digital</span>, 
              distribuidora líder de contenidos alternativos para cines en España.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link 
                to="/cinema/register" 
                className="px-8 py-4 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] transition-all flex items-center gap-2"
                data-testid="hero-register-btn"
              >
                Únete a Bravo <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/catalogo" 
                className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
                data-testid="hero-catalog-btn"
              >
                Ver catálogo
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center group">
                <stat.icon className="w-10 h-10 text-[#e4002b] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <p className="text-5xl font-bold text-white mb-2">{stat.number}</p>
                <p className="text-white/60 uppercase tracking-wider text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Version Digital */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-1 bg-[#e4002b]" />
                <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Versión Digital</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Larga trayectoria en el <span className="text-[#e4002b]">sector cultural</span>
              </h2>
              
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                Versión Digital cuenta con una larga trayectoria en el sector, especializándose en la 
                distribución de ópera, ballet, conciertos, documentales de arte y otros eventos en 
                directo y diferido.
              </p>
              
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Con Bravo, hemos creado una plataforma intuitiva y eficiente diseñada para simplificar 
                el proceso de contratación y recepción de estos contenidos.
              </p>

              {/* Content types */}
              <div className="flex flex-wrap gap-3">
                {contentTypes.map((type, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10"
                  >
                    <type.icon className="w-4 h-4" style={{ color: type.color }} />
                    <span className="text-white/80 text-sm">{type.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1536852281373-656571dde83b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjb25jZXJ0JTIwY3Jvd2QlMjByZWQlMjBsaWdodHMlMjBzbW9rZSUyMGF1ZGllbmNlJTIwbGl2ZSUyMGV2ZW50fGVufDB8fHx8MTc3MDc0NTc2MHww&ixlib=rb-4.1.0&q=85" 
                alt="Público en concierto con luces rojas"
                className="w-full h-full object-cover"
              />
              {/* Gradient overlays for integration */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/50 via-transparent to-transparent" />
              
              {/* Badge */}
              <div className="absolute bottom-8 left-8 bg-[#e4002b] rounded-xl p-5 shadow-2xl">
                <p className="text-white/90 text-sm">Distribuidora líder en</p>
                <p className="text-white text-2xl font-bold">España</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              La Solución <span className="text-[#e4002b]">Bravo</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Permite a salas de cine y auditorios acceder a contenido cultural de primer nivel
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="group relative bg-[#0a0a0a] rounded-2xl p-8 hover:bg-[#1a1a1a] transition-all overflow-hidden border border-white/5 hover:border-white/10"
              >
                <div 
                  className="absolute top-0 left-0 w-full h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: feature.color }}
                />
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon className="w-8 h-8" style={{ color: feature.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-white/60 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1 rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1762541693135-fb989de961e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxjaW5lbWElMjBwcm9qZWN0b3IlMjBsaWdodCUyMGJlYW0lMjBwcm9qZWN0aW9uJTIwcm9vbSUyMG1vdmllJTIwdGhlYXRlcnxlbnwwfHx8fDE3NzA3NDU1NjJ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Proyector de cine con haz de luz"
                className="w-full h-full object-cover"
              />
              {/* Gradient overlays for integration */}
              <div className="absolute inset-0 bg-gradient-to-l from-[#0a0a0a] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/50 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-1 bg-[#e4002b]" />
                <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Nuestra Misión</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Tu socio tecnológico y de <span className="text-[#e4002b]">contenidos</span>
              </h2>
              
              <p className="text-white/70 text-lg leading-relaxed mb-6">
                En Bravo, nuestro objetivo es ser tu socio tecnológico y de contenidos, proporcionándote 
                las herramientas necesarias para ofrecer experiencias únicas a tus espectadores.
              </p>
              
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Con el respaldo de la experiencia de Versión Digital, Bravo es la puerta de entrada 
                a un mundo de posibilidades para tu sala.
              </p>

              <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#e4002b]/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-[#e4002b]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-1">Experiencias únicas</h4>
                    <p className="text-white/60 text-sm">
                      Ofrecemos contenido cultural exclusivo que atrae nuevos públicos a tu sala
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              El mejor contenido <span className="text-[#e4002b]">cultural</span>
            </h2>
            <p className="text-white/60 text-lg">
              Ópera, ballet, conciertos y teatro desde los mejores escenarios del mundo
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2">
              <img 
                src="https://images.unsplash.com/photo-1758670331763-b0f3cd7c1a8d?w=800&q=80" 
                alt="Ballet performance"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=400&q=80" 
                alt="Orchestra"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&q=80" 
                alt="Concert"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1760542940745-a4b24d5e9b5b?w=400&q=80" 
                alt="Ballet"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1769981420455-5e87a0407d6c?w=400&q=80" 
                alt="Opera house"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#e4002b]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para transformar tu sala?
          </h2>
          <p className="text-white/90 text-xl mb-10">
            Únete a más de 125 cines que ya ofrecen los mejores espectáculos culturales del mundo
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/cinema/register" 
              className="px-10 py-4 bg-white text-[#e4002b] font-bold rounded-lg hover:bg-white/90 transition-all text-lg"
              data-testid="cta-register-btn"
            >
              Registrar mi cine
            </Link>
            <Link 
              to="/condiciones" 
              className="px-10 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all text-lg"
              data-testid="cta-conditions-btn"
            >
              Ver condiciones
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}
    </div>
  );
};

export default SobreNosotrosPage;
